import {
  BerthResponseDto,
  BerthSide,
  CargoType,
  CarrierVisitStatus,
  VesselDto,
  VesselVisitDto,
} from '@planning/app/api'
import {
  getPixelsPerHour,
  getXAxisAndWidthForVisit,
  isDateWithinRange,
  isNumberWithinRange,
} from '@planning/components/carrier-visit-planning/carrier-visit-planning.helper'
import { IVesselPlanning } from '@planning/components/carrier-visit-planning/CarrierVisitPlanning.model'
import { VesselVisitBerthAndTimingFormStore } from '@planning/pages/VesselVisit/Stores/VesselVisitBerthAndTimingFormStore'
import { VesselVisitFormStoreType } from '@planning/pages/VesselVisit/Stores/VesselVisitFormStore'
import { BerthItemStore } from '@planning/rt-stores/berth/BerthItemStore'
import { PierItemStore } from '@planning/rt-stores/berth/PierItemStore'
import { VesselVisitItemStore } from '@planning/rt-stores/vesselVisit/VesselVisitItemStore'
import { IVesselVisitPlanningFormData } from '@planning/rt-stores/vesselVisit/VesselVisitPlanningStore'
import { isOverlappingBerth } from '@tom-ui/utils'
import dayjs, { Dayjs } from 'dayjs'
import _ from 'lodash'
import { action, computed, makeObservable, observable } from 'mobx'

type DaysVisualization = 4 | 7
// [Test] TODO: UT the stores pls!
export class BerthPlanningViewStore {
  isViewMode = false
  pixelPerWaterMark = 1
  startDate = dayjs().subtract(1, 'day').startOf('day').toDate()
  daysVisualization = 4
  berthViewTotalWidth = 700
  selectedPierId = ''
  selectedBerths: BerthResponseDto[] = []
  selectedQmmFrom = 0
  selectedQmmTo = 0
  isDialogOpen = false
  vesselVisitSelectedId = 0
  selectedVessels: VesselDto[] = []
  nextVesselVisitIdToBeEdited = 0

  currentFormValues?: IVesselVisitPlanningFormData
  vesselVisitBerthAndTimingFormStore?: VesselVisitBerthAndTimingFormStore

  constructor(
    public berthItemStore: BerthItemStore,
    private vesselVisitItemStore: VesselVisitItemStore,
    private pierItemStore: PierItemStore,
    daysVisualization?: DaysVisualization,
  ) {
    makeObservable(this, {
      isViewMode: observable,
      startDate: observable,
      daysVisualization: observable,
      berthViewTotalWidth: observable,
      selectedPierId: observable,
      selectedBerths: observable,
      selectedQmmFrom: observable,
      selectedQmmTo: observable,
      currentFormValues: observable,
      isDialogOpen: observable,
      vesselVisitSelectedId: observable,
      selectedVessels: observable,
      nextVesselVisitIdToBeEdited: observable,

      piers: computed,
      piersWithBerths: computed,
      startWaterMark: computed,
      endWaterMark: computed,
      plannedVisits: computed,
      endDate: computed,
      pixelPerHour: computed,
      berths: computed,
      berthsByQmmRange: computed,
      formValuesMappedToVesselVisitDto: computed,
      startDateAsDayjs: computed,

      setViewMode: action,
      applyFormStoreDataToPlanning: action,
      toggleConfirmationDialog: action,
      setDaysVisualization: action,
      setBerthViewTotalWidth: action,
      setStartDate: action,
      loadNextDate: action,
      loadPrevDate: action,
      goToToday: action,
      setStartDateFromDayjs: action,
      setSelectPierId: action,
      setFormValues: action,
      setVesselVisitSelectedId: action,
      setSelectedVessels: action,
      setNextVesselVisitIdToBeEdited: action,
      setPierByWaterMark: action,
      setSelectedBerths: action,
      setSelectedQmmFrom: action,
      setSelectedQmmTo: action,

      setVesselVisitBerthAndTimingFormStore: action,
    })

    this.setDaysVisualization(daysVisualization ?? 4)
  }

  setVesselVisitBerthAndTimingFormStore(
    vesselVisitBerthAndTimingFormStore: VesselVisitBerthAndTimingFormStore,
  ) {
    this.vesselVisitBerthAndTimingFormStore = vesselVisitBerthAndTimingFormStore
  }

  toggleConfirmationDialog = (shouldOpen: boolean) => {
    this.isDialogOpen = shouldOpen
  }

  setDaysVisualization(value: number) {
    this.daysVisualization = value
  }

  setBerthViewTotalWidth(value: number) {
    this.berthViewTotalWidth = value
  }

  setStartDate(date: Date) {
    const midnightDate = dayjs(date).startOf('day').toDate()
    this.startDate = midnightDate
    this.fetchVisits()
  }

  loadNextDate() {
    const newDate = dayjs(this.startDate).add(this.daysVisualization, 'day').toDate()
    this.setStartDate(newDate)
  }

  loadPrevDate() {
    const newDate = dayjs(this.startDate).subtract(this.daysVisualization, 'day').toDate()
    this.setStartDate(newDate)
  }

  goToToday() {
    const today = dayjs().subtract(1, 'day').startOf('day').toDate()
    this.setStartDate(today)
  }

  get startDateAsDayjs() {
    return dayjs(this.startDate)
  }

  setStartDateFromDayjs(date: Dayjs) {
    this.setStartDate(date.toDate())
  }

  setViewMode(isViewMode: boolean) {
    this.isViewMode = isViewMode
  }

  setSelectPierId(id: string) {
    this.selectedPierId = id
  }

  setSelectedBerths(berths: BerthResponseDto[]) {
    this.selectedBerths = berths
  }

  setSelectedQmmFrom(qmmFrom: number) {
    this.selectedQmmFrom = +qmmFrom
  }

  setSelectedQmmTo(qmmTo: number) {
    this.selectedQmmTo = +qmmTo
  }

  setVesselVisitSelectedId(id: number) {
    this.vesselVisitSelectedId = id
  }

  setSelectedVessels(vessels: VesselDto[]) {
    this.selectedVessels = vessels
  }

  setNextVesselVisitIdToBeEdited(id: number) {
    this.nextVesselVisitIdToBeEdited = id
  }

  // [Test] TODO: UT
  applyFormStoreDataToPlanning(form: VesselVisitFormStoreType, selectedVessel?: VesselDto): void {
    const eta = this.combineDateAndTime(form.berthAndTiming?.etaDate, form.berthAndTiming?.etaTime)
    const etb = this.combineDateAndTime(form.berthAndTiming?.etbDate, form.berthAndTiming?.etbTime)
    const etd = this.combineDateAndTime(form.berthAndTiming?.etdDate, form.berthAndTiming?.etdTime)

    const formData: IVesselVisitPlanningFormData = {
      eta: eta,
      etb: etb,
      etd: etd,
      importDraft: Number(form.berthAndTiming?.draftArrival),
      exportDraft: Number(form.berthAndTiming?.draftDeparture),
      qmmFrom: Number(form.berthAndTiming?.qmmFrom),
      qmmTo: Number(form.berthAndTiming?.qmmTo),
      vesselIds: form.generalInformation?.vesselIds ?? [],
      cargoType: form.cargo?.cargoType ?? CargoType.Container,
      dischargeEstimate: form.cargo?.dischargeEstimate ?? 0,
      loadEstimate: form.cargo?.loadEstimate ?? 0,
      berthSide: form.berthAndTiming?.dockingSide ?? BerthSide.Port,
      tripIds:
        form.generalInformation?.voyages?.map(v => ({
          inbound: v.inboundNumber ?? '',
          outbound: v.outboundNumber ?? '',
        })) ?? [],
      vesselServiceId: form.generalInformation?.vesselServiceId ?? null,
      portRotation: form.generalInformation?.portRotation ?? null,
    }

    this.currentFormValues = formData
    this.setSelectedVessels([selectedVessel!])

    if (formData?.eta && formData?.etd && formData.qmmFrom && formData.qmmTo) {
      const date = formData.eta.format()

      this.setStartDateIfNewDateHasADifferentStart(date)
      this.setPierByWaterMark(formData.qmmFrom, formData.qmmTo)
    }
  }

  setFormValues(value?: IVesselVisitPlanningFormData) {
    this.currentFormValues = value

    if (value?.eta && value?.etd && value.qmmFrom && value.qmmTo) {
      const date = value.eta.format()
      this.setStartDateIfNewDateHasADifferentStart(date)
      this.setPierByWaterMark(value.qmmFrom, value.qmmTo)
    }
  }

  public setStartDateIfNewDateHasADifferentStart(date?: string) {
    if (
      date &&
      dayjs(this.startDate.toDateString()).diff(dayjs(date).toDate().toDateString(), 'days') !== 0
    ) {
      this.setStartDate(dayjs(date).toDate())
    }
  }

  public setPierByWaterMark(qmmFrom: number, qmmTo: number) {
    if (this.selectedPierId) {
      return
    }

    for (const pierId in this.berthItemStore.berthsByPier) {
      if (Object.prototype.hasOwnProperty.call(this.berthItemStore.berthsByPier, pierId)) {
        const berths = this.berthItemStore.getBerthsByPier(pierId)
        const min = berths[0]?.quayMeterMarkFrom ?? 0
        const max = berths[berths.length - 1]?.quayMeterMarkTo ?? 0

        const isFullyContained = qmmFrom >= min && qmmTo <= max

        if (isFullyContained) {
          this.setSelectPierId(pierId)
          break
        }
      }
    }
  }

  async fetchVisits() {
    const fromDate = dayjs(this.startDate).subtract(7, 'day').toDate()
    const toDate = dayjs(this.startDate).add(7, 'day').toDate()

    await this.vesselVisitItemStore.fetch(fromDate, toDate)
  }

  public async fetchPiers() {
    this.pierItemStore.fetch()
  }

  get piers() {
    return _(this.pierItemStore.elements)
      .values()
      .value()
      .map(pierItem => pierItem.data)
  }

  get piersWithBerths() {
    const pierIdsWithBerths = this.berthItemStore.piersIds

    return _(this.pierItemStore.elements)
      .values()
      .filter(pierItem => pierIdsWithBerths.includes(pierItem.data.id))
      .map(pierItem => pierItem.data)
      .value()
  }

  public get plannedVisits(): IVesselPlanning[] {
    const formVisit = this.formValuesMappedToVesselVisitDto
    let exisitingVisits = this.vesselVisits.filter(
      visit => visit.status !== CarrierVisitStatus.Cancelled,
    )

    if (formVisit) {
      exisitingVisits = exisitingVisits.filter(x => x.id !== formVisit?.id)

      // Prevents the card from flashing red (false-positive overlap) when submitting a new vessel visit.
      // This happens because the newly created visit arrives via SignalR while the provisional formVisit is still active.
      if (formVisit.id === 0) {
        exisitingVisits = exisitingVisits.filter(x => {
          const xTimings = this.getEffectiveTimings(x)
          const fTimings = this.getEffectiveTimings(formVisit)

          const isSameEta =
            !!xTimings.eta && !!fTimings.eta && dayjs(xTimings.eta).isSame(fTimings.eta)
          const isSameEtd =
            !!xTimings.etd && !!fTimings.etd && dayjs(xTimings.etd).isSame(fTimings.etd)

          return !(
            x.qmmFrom === formVisit.qmmFrom &&
            x.qmmTo === formVisit.qmmTo &&
            isSameEta &&
            isSameEtd
          )
        })
      }

      exisitingVisits = [...exisitingVisits, formVisit]
    }

    const visits = _(exisitingVisits)
      .filter(visit => {
        const timings = this.getEffectiveTimings(visit)

        const isOutsideDateRange =
          dayjs(timings.eta).diff(this.endDate, 'hours') >= 0 ||
          dayjs(timings.etd).diff(this.startDate, 'hours') <= 0

        const isOutsideWaterMarkRange =
          (visit && visit.qmmFrom && visit.qmmFrom >= this.endWaterMark) ||
          (visit && visit.qmmTo && visit.qmmTo <= this.startWaterMark)

        const pierId = this.getPierId(visit)
        const isDifferentPier = pierId !== this.selectedPierId

        return !(isOutsideDateRange || isOutsideWaterMarkRange || isDifferentPier)
      })
      .orderBy(x => this.getEffectiveTimings(x).eta)
      .value()

    return visits
      .map(visit => {
        const timings = this.getEffectiveTimings(visit)
        const xAxis = this.getXAxisPositionsForVisit(visit)
        const yAxis = this.getYAxisPositionsForVisit(visit)

        return {
          id: visit.id,
          name: visit.identifier ?? '-',
          x: xAxis.x,
          width: xAxis.width,
          y: yAxis.y,
          height: yAxis.height,
          cargoType: visit.cargoType,
          status: visit.status,
          eta: timings.eta ?? '',
          etaLabel: timings.etaLabel,
          etb: timings.etb ?? '',
          etbLabel: timings.etbLabel,
          etd: timings.etd ?? '',
          etdLabel: timings.etdLabel,
          draftIn: visit.importDraft ?? 0,
          draftOut: visit.exportDraft ?? 0,
          qmmFrom: visit.qmmFrom ?? 0,
          qmmTo: visit.qmmTo ?? 0,
          loadEstimate: visit.loadEstimate ?? 0,
          dischargeEstimate: visit.dischargeEstimate ?? 0,
          restowEstimate: visit.restowEstimate ?? 0,
          overlaps: this.doesVisitHasAnOverlapWithAnotherVisit(
            exisitingVisits,
            visit,
            this.getPierId(visit),
          ),
          selected: visit.id === this.vesselVisitSelectedId,
        }
      })
      .filter(visit => visit.width > 0 && visit.height > 0)
  }

  public get endDate() {
    return dayjs(this.startDate).add(this.daysVisualization, 'day').subtract(1, 'minute').toDate()
  }

  public get startWaterMark() {
    return this.berths[0]?.quayMeterMarkFrom ?? 1
  }

  public get endWaterMark() {
    return this.berths[this.berths.length - 1]?.quayMeterMarkTo ?? 1000
  }

  public get pixelPerHour() {
    return getPixelsPerHour(this.berthViewTotalWidth, this.daysVisualization)
  }

  public get berths() {
    return (
      _(this.berthItemStore.berthsByPier[this.selectedPierId])
        ?.map(b => b.data)
        .value() ?? []
    )
  }

  public get berthsByQmmRange() {
    if (this.selectedQmmTo < this.selectedQmmFrom) return []

    const berths = _.values(this.berths)

    return berths.filter(b =>
      isOverlappingBerth(
        this.selectedQmmFrom,
        this.selectedQmmTo,
        b.quayMeterMarkFrom,
        b.quayMeterMarkTo,
      ),
    )
  }

  public get vesselVisits() {
    return (
      _(this.vesselVisitItemStore.elements)
        ?.map(b => b.data)
        .value() ?? []
    )
  }

  private getPixelsBetweenWaterMarks(startWaterMark: number, endWaterMark: number) {
    return this.pixelPerWaterMark * (endWaterMark - startWaterMark)
  }

  private getXAxisPositionsForVisit(visit: VesselVisitDto) {
    const timings = this.getEffectiveTimings(visit)
    const eta = timings.eta
    const etd = timings.etd

    return getXAxisAndWidthForVisit(this.startDate, this.endDate, this.pixelPerHour, eta, etd)
  }

  private getYAxisPositionsForVisit(visit: VesselVisitDto) {
    let waterMarkFrom = visit.qmmFrom ?? 0
    const waterMarkTo = visit.qmmTo ?? 0
    let yAxis = this.getPixelsBetweenWaterMarks(this.startWaterMark, waterMarkFrom)

    if (yAxis < 0) {
      yAxis = 0
      waterMarkFrom = this.startWaterMark
    }

    let height = this.getPixelsBetweenWaterMarks(waterMarkFrom, waterMarkTo)
    if (this.getPixelsBetweenWaterMarks(this.endWaterMark, waterMarkTo) > 0) {
      height = this.getPixelsBetweenWaterMarks(waterMarkFrom, this.endWaterMark)
    }

    return {
      y: yAxis,
      height: height,
    }
  }

  private doesVisitHasAnOverlapWithAnotherVisit(
    otherVisits: VesselVisitDto[],
    visit: VesselVisitDto,
    visitPierId?: string,
  ): boolean {
    if (!visitPierId) return false

    const vTimings = this.getEffectiveTimings(visit)
    return otherVisits
      .filter(x => x.id !== visit.id)
      .some(x => {
        if (visit.qmmFrom == null || visit.qmmTo == null || x.qmmFrom == null || x.qmmTo == null) {
          return false
        }

        const pierId = this.getPierId(x)
        if (pierId !== visitPierId) {
          return false
        }

        const overlapsOnXAxis =
          isDateWithinRange(vTimings.eta, x.eta, x.etd) ||
          isDateWithinRange(vTimings.etd, x.eta, x.etd) ||
          isDateWithinRange(x.eta, vTimings.eta, vTimings.etd)

        const overlapsOnYAxis =
          isNumberWithinRange(visit.qmmFrom, x.qmmFrom, x.qmmTo) ||
          isNumberWithinRange(visit.qmmTo, x.qmmFrom, x.qmmTo) ||
          isNumberWithinRange(x.qmmFrom, visit.qmmFrom, visit.qmmTo)

        return overlapsOnXAxis && overlapsOnYAxis
      })
  }

  private getPierId(visit: VesselVisitDto) {
    const berthId = visit.berthIds?.[0] ?? -1
    const berth = _(this.berthItemStore.elements).get(berthId, undefined)
    const pierId = berth?.data.pierId ?? undefined
    return pierId
  }

  // [Test] TODO: UT
  get formValuesMappedToVesselVisitDto(): VesselVisitDto | undefined {
    if (!this.currentFormValues) return

    const existingVisit = this.vesselVisits.find(x => x.id === this.vesselVisitSelectedId)
    const vesselIds = this.currentFormValues?.vesselIds
    const selectedVessels = this.selectedVessels

    let identifier = '-'

    if (
      Array.isArray(vesselIds) &&
      vesselIds.length > 0 &&
      Array.isArray(selectedVessels) &&
      selectedVessels.length > 0
    ) {
      const found = selectedVessels.find(x => x && vesselIds.includes(x.id))
      if (found?.name) {
        identifier = found.name
      }
    }

    return {
      id: this.currentFormValues.id ?? this.vesselVisitSelectedId,
      cargoType: this.currentFormValues.cargoType,
      status: existingVisit?.status ?? CarrierVisitStatus.Expected,
      // if the visit ata is set, the eta should be ignored for display on the timeline
      eta: existingVisit?.ata ?? this.currentFormValues.eta?.format(),
      ata: existingVisit?.ata ?? null,
      etb: this.currentFormValues.etb?.format(),
      // if the visit atd is set, the etd should be ignored for display on the timeline
      etd: existingVisit?.atd ?? this.currentFormValues.etd?.format(),
      atd: existingVisit?.atd ?? null,
      importDraft: this.currentFormValues.importDraft,
      exportDraft: this.currentFormValues.exportDraft,
      qmmFrom: this.currentFormValues.qmmFrom,
      qmmTo: this.currentFormValues.qmmTo,
      identifier,
      carrierIds: existingVisit?.carrierIds ?? [],
      dischargeConfirmed: existingVisit?.dischargeConfirmed ?? 0,
      dischargeTotal: existingVisit?.dischargeTotal ?? 0,
      dischargeEstimate: this.currentFormValues?.dischargeEstimate ?? 0,
      loadEstimate: this.currentFormValues?.loadEstimate ?? 0,
      loadConfirmed: existingVisit?.loadConfirmed ?? 0,
      loadTotal: existingVisit?.loadTotal ?? 0,
      berthIds: this.selectedBerths.map(b => b.id) ?? existingVisit?.berthIds ?? [],
      orderIds: [],
    }
  }

  private combineDateAndTime(date?: Dayjs | null, time?: Dayjs | null): Dayjs | undefined {
    if (!date || !time) return undefined

    return date.set('hour', time.hour()).set('minute', time.minute()).set('second', time.second())
  }

  public getEffectiveTimings(visit: VesselVisitDto) {
    const isATA = !!visit.ata
    const isATD = !!visit.atd
    const isATB = !!visit.atb

    return {
      eta: visit.ata ?? visit.eta,
      etaLabel: isATA ? 'ATA' : 'ETA',

      etb: visit.atb ?? visit.etb,
      etbLabel: isATB ? 'ATB' : 'ETB',

      etd: visit.atd ?? visit.etd,
      etdLabel: isATD ? 'ATD' : 'ETD',
    }
  }

  public setBerthAndPierAndQmm = (values?: BerthResponseDto[]) => {
    const firstBerth = values?.[0]
    const lastBerth = values?.[values.length - 1]

    const firstBerthQuayMeterMarkFrom = firstBerth?.quayMeterMarkFrom
    const lastBerthQuayMeterMarkTo = lastBerth?.quayMeterMarkTo

    this.vesselVisitBerthAndTimingFormStore?.berths.setValue(values ?? [])
    this.setSelectedBerths(values ?? [])

    this.calculateQmmBasedOnBerthAndVesselLength(
      firstBerthQuayMeterMarkFrom,
      lastBerthQuayMeterMarkTo,
    )

    if (firstBerth?.pierId) {
      this.setSelectPierId(firstBerth.pierId)
    }
  }

  public calculateQmmBasedOnBerthAndVesselLength = (
    firstBerthQuayMeterMarkFrom?: number,
    lastBerthQuayMeterMarkTo?: number,
    ignoreVesselLength = false,
  ) => {
    const selectedVessel = this.selectedVessels?.[0]
    const nFirstBerthQuayMeterMarkFrom = firstBerthQuayMeterMarkFrom ?? 0

    this.vesselVisitBerthAndTimingFormStore?.qmmFrom.setValue(firstBerthQuayMeterMarkFrom ?? '')
    this.setSelectedQmmFrom(nFirstBerthQuayMeterMarkFrom)

    if (selectedVessel && !ignoreVesselLength) {
      const vesselLength = selectedVessel.length || 0
      const calculatedQmmTo = nFirstBerthQuayMeterMarkFrom + vesselLength

      this.vesselVisitBerthAndTimingFormStore?.qmmTo.setValue(
        nFirstBerthQuayMeterMarkFrom + vesselLength,
      )
      this.setSelectedQmmTo(calculatedQmmTo)
    } else {
      this.vesselVisitBerthAndTimingFormStore?.qmmTo.setValue(lastBerthQuayMeterMarkTo ?? '')
      this.setSelectedQmmTo(lastBerthQuayMeterMarkTo ?? 0)
    }
  }
}
